import { BlockUiService } from 'app/shared/components/block-ui/block-ui.service';
import { catchError, filter, take } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguagesService } from './../languages/languages.service';
import { UserRolesEnum } from '@collections/users/enums';
import { Injectable, Optional, createNgModule, ApplicationRef, inject, NgZone, EnvironmentInjector, signal } from '@angular/core';
import { Observable, iif, of, BehaviorSubject, from } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';
import { AuthService } from '@shared/auth/auth.service';
import { UserInfo } from '@shared/services/users/types/user-info';
import { UserInfoGQL } from './user-info.gql.service';
import { Apollo } from 'apollo-angular';
import type { MatDialogsHelperService } from 'app/shared/mat-dialogs-helper/mat-dialogs-helper.service';
import { AnalyticsService } from '../google-tag-manager/analytics/analytics.service';
import { ICompany } from '@collections/companies/interfaces';
import { FollowCompanyGQL } from './follow-company.gql.service';
import { UnFollowCompanyGQL } from './unfollow-company.gql.service';
import { IUser } from '@collections/users/interfaces';
import { clearByRegEx } from 'app/shared/services/common/cache.utilities';
import { UserInteractionService } from '../user-interaction/user-interaction.service';
import { Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Platform } from '@angular/cdk/platform';
import { injectAsync } from '../inject-async';
import { environment } from '@env/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { IProduct } from '@collections/products/interfaces';
import { saveProductGQL } from './save-product.gql.service';
import { removeProductGQL } from './remove-product.gql.service';
import { saveNewsGQL } from './save-news.gql.service';
import { removeNewsGQL } from './remove-news.gql.service';
import { INews } from '@collections/news/interfaces';
 
@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private _currentUserRoles: string[];
  private _onUserChangeSubject: BehaviorSubject<UserInfo>;
  private _currentCompanyId = '';
  private _currentUserId: string;
  private matDialogsHelperService: MatDialogsHelperService;
  private readonly environmentInjector = inject(EnvironmentInjector);
  private followCompanyGql: FollowCompanyGQL;
  private unFollowCompanyGql: UnFollowCompanyGQL;

  private snackBar: MatSnackBar;

  get onUserChange(): Observable<UserInfo> {
    return this._onUserChangeSubject.asObservable();
  }

  get currentUserId(): string {
    return this._currentUserId;
  }

  private _currentUser: UserInfo;
  isLoggedIn = false;
  ngZone = inject(NgZone);
  userAction = toSignal(this.userInteractionService.onUserAction$);

  constructor(
    private appRef: ApplicationRef,
    private authService: AuthService,
    private languagesService: LanguagesService,
    private userInfoGQL: UserInfoGQL,
    private router: Router,
    private blockUiService: BlockUiService,
    @Optional() public analyticsService: AnalyticsService,
    private userInteractionService: UserInteractionService,
    @Optional() private swUpdate: SwUpdate,
    private apollo: Apollo,
    private platform: Platform,
    private saveProductGQL: saveProductGQL,
    private removeProductGQL: removeProductGQL,
    private saveNewsGQL: saveNewsGQL,
    private removeNewsGQL: removeNewsGQL
  ) {

    this.userInteractionService.onUserAction$
    .subscribe(() => {
      this.initSnackBar().subscribe(() => {
        if (this.swUpdate.isEnabled) {
          this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe(() => {
            this.snackBar.open(
              $localize`New Application version is available. Reloading...`, $localize`Warning`, {
                duration: 4000,
                verticalPosition: 'top'
            }).afterDismissed().subscribe(() => {
              // reload service worker
              document.location.reload();
            });
          });
        }
      });
    });

    this._currentUserRoles = [];
    this._onUserChangeSubject = new BehaviorSubject(null);
    this.authService.authTokenChange
      .pipe(
        tap((token) => {
          this._currentUserRoles = token?.roles ?? [];
        }),
        switchMap(token =>
          iif(() => (token !== null),
              this.userInfoGQL.watch({
                id: token?.id,
                lang: this.languagesService.getCurrentLangCode()
              }, {
                fetchPolicy: 'cache-first'
              }).valueChanges
              .pipe(
                map(result => result.data.userById),
                tap(user => {
                  this._currentUserId = user._id;
                  const currentRoles = user.roles;
                  // se i ruoli su database sono diversi da quelli nel token, che persiste nel localstorage,
                  // eseguo il refresh del token
                  if (!this.rolesAreEquals(token.roles, currentRoles)) {
                    this.authService.refreshToken()
                    .subscribe(token => this._currentUserRoles = currentRoles);
                  } else {
                    this._currentUserRoles = currentRoles;
                  }
                  if (token.validated !== user.validated) {
                    const { cache } = this.apollo.client;
                    cache.modify({
                      id: cache.identify(user),
                      fields: {
                        validated() {
                          return token.validated;
                        },
                      }
                    });
                  }
                })
              ),
            of(null)
            .pipe(
              tap(() => this._currentUserRoles = []) // logout
            )
          ))
      ).subscribe(user => this._onUserChangeSubject.next(user));

      this.onUserChange
      .pipe(
        filter(user => !!user),
        take(1)
      ).subscribe(async () => {
        const [followCompanyGQL, unFollowCompanyGQL] = await Promise.all([
          injectAsync(this.environmentInjector, () =>
            import('./follow-company.gql.service').then((c) => c.FollowCompanyGQL),
          ),
          injectAsync(this.environmentInjector, () =>
            import('./unfollow-company.gql.service').then((c) => c.UnFollowCompanyGQL),
          )
        ]);
        this.followCompanyGql = followCompanyGQL;
        this.unFollowCompanyGql = unFollowCompanyGQL;
      })

      this.onUserChange
      .subscribe(async (user) => {
        this._currentUser = user;
        /* Deactivated sigup-wizard since the reg-wall was added */
        // if(this.platform.isBrowser) {
        //   if(user) {
        //     this.resetTimer();
        //   } else {
        //     this.startTimerAndOpenLogin();
        //   }
        // }

      });
  }

  logout(): void {
    this.authService.invalidate();
    this.router.navigate(['/']);
  }

  openCompanyWizard(company?: ICompany) {
    this.blockUiService.show($localize`Loading...`);
    this.ensureDialogService()
    .pipe(
      switchMap(() => {
        return this.matDialogsHelperService.openCompanyWizard(company)
      })
    ).subscribe();
  }

  openRegister(company?: ICompany){
    this.blockUiService.show($localize`Loading...`);
    this.ensureDialogService()
    .pipe(
      switchMap(() => {
        return this.matDialogsHelperService.openRegister(company)
      })
    ).subscribe();
  }

  updateToken(token: string): void {
    this.authService.setauthToken(token);
  }

  public isInRole(roles: UserRolesEnum[]): boolean {
    if (!roles) {
      return true;
    }
    const _roles: string[] = roles.map(r => r === UserRolesEnum.stand_admin ? UserRolesEnum.stand_admin + this._currentCompanyId : r);
    return this._currentUserRoles.some((role) => _roles.includes(role));
  }


  canLoadBusinnesSection(): boolean {
    const permissionRoles = [UserRolesEnum.customer, UserRolesEnum.admin];
    return this._currentUserRoles.some((role: UserRolesEnum) => permissionRoles.includes(role));
  }

  canActivateModule(companyId: string): boolean {
    const userRoles = this._currentUserRoles;
    if (userRoles.includes(UserRolesEnum.admin)) {
      return true;
    }
    const standAdminRole = UserRolesEnum.stand_admin + companyId;
    if (userRoles.includes(standAdminRole)) {
      return true;
    }
    return false;
  }


  private rolesAreEquals(roles1: string[], roles2: string[]): boolean {
    return JSON.stringify(roles1.slice().sort()) === JSON.stringify(roles2.slice().sort());
  }

  setCurrentCompanyRole(companyId: string): void {
    this._currentCompanyId = companyId;
  }

  checkPermission(cb: Function, roles = [UserRolesEnum.basic]): void {
    if (this.isInRole(roles)) {
      cb();
    } else {
      this.openLogin()
      .subscribe((loggedIn) => loggedIn ? cb() : null);
    }
  }

  checkPermissionLoad(roles = [UserRolesEnum.basic]): boolean {
    if (this.isInRole(roles)) {
      return true;
    } else {
      return false;
    }
  }

  private timer: any; // dichiarazione del timer

  resetTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  startTimerAndOpenLogin(cb: Function = () => {}): void {
  
    // Resetta il timer esistente se presente (resetta il timer ogni volta che viene chiamata la startTimerAndOpenLogin) (serve per evitare che quando apro il sito mi apre il popup due volte)

    if (!this._currentUser && !this.timer && !environment.debugOn) {               //se non è loggato allora appare il popup di 60s
/*       if(this.isInRole([UserRolesEnum.basic])){
        return;
      } */
      this.ngZone.runOutsideAngular(() => {
        this.timer = setTimeout(() => {
          this.openLogin().subscribe((loggedIn) => {
            if (!loggedIn) {
              this.resetTimer();
              this.ngZone.run(() => {
                this.startTimerAndOpenLogin(cb);
              })
            } else {
              cb();
            }
          });
        }, 60000); // 60 secondi
      })

    }
  }

  openLogin(): Observable<boolean> {
    this.blockUiService.show($localize`Loading...`);
    return this.ensureDialogService()
    .pipe(
      switchMap(() => {
        return this.matDialogsHelperService.openLogin();
      })
    );
  }

  confirmAction(options: any): Observable<any> {
    return this.ensureDialogService()
    .pipe(
      switchMap(() => {
        return this.matDialogsHelperService.confirm(options);
      })
    );
  }

  private ensureDialogService(): Observable<boolean> {
    return this.matDialogsHelperService ?
    of(true) :
    from(import('app/shared/mat-dialogs-helper/mat-dialogs-helper.module'))
    .pipe(
      map(({MatDialogsHelperModule}) => {
        return createNgModule(MatDialogsHelperModule, this.appRef.injector);
      }),
      switchMap(moduleRef => {
        return from(import('app/shared/mat-dialogs-helper/mat-dialogs-helper.service'))
          .pipe(
            tap(({MatDialogsHelperService}) => {
              this.matDialogsHelperService = moduleRef.injector.get<MatDialogsHelperService>(MatDialogsHelperService)
            })
          )
      }),
      map(() => true),
      take(1)
    );
  }

  initSnackBar(): Observable<boolean> {
    return from(import('@angular/material/snack-bar'))
    .pipe(
      map(({MatSnackBarModule}) => {
        return createNgModule(MatSnackBarModule, this.appRef.injector);
      }),
      switchMap(moduleRef => {
        return from(import('@angular/material/snack-bar'))
          .pipe(
            map(({MatSnackBar}) => {
              this.snackBar = moduleRef.injector.get<MatSnackBar>(MatSnackBar);
              return true;
            })
          )
      }),
      take(1)
    );
  }

  toggleFollowCompany(company: ICompany, cb = (u: IUser) => {}): void {
    const lang = this.languagesService.getCurrentLangCode();
    this.checkPermission(() => {
      if (!this.isFollowedCompany(company._id)) {
        this.analyticsService.ecTrackAddRemoveToCart(company, true);
        this.followCompanyGql.mutate({
          id: this.currentUserId,
          lang: lang,
          companyId: company._id
        }, {
          update: (cache) => {
            this.clearFollowedCompaniesCache(cache);
          }
        })
        .pipe(
          map(res => res.data.followCompany)
        )
        .subscribe(u => cb(u));
      } else {
        this.analyticsService.ecTrackAddRemoveToCart(company, false);
        this.unFollowCompanyGql.mutate({
          id: this.currentUserId,
          lang: lang,
          companyId: company._id
        }, {
          update: (cache) => {
            this.clearFollowedCompaniesCache(cache);
          }
        })
        .pipe(
          map(res => res.data.unFollowCompany)
        )
        .subscribe(u => cb(u));
      }
    });
  }

  isFollowedCompany(companyId: string): boolean {
    return !!this._currentUser?.profile?.followedCompanies?.find(fc => fc._id === companyId);
  }

  private clearFollowedCompaniesCache(cache: any): void {
    const key = `^followedCompaniesByUser:.*\"userId\":\"${this.currentUserId}\".*`;
    clearByRegEx(cache,key);
    cache.gc();
  }

  toggleSaveProduct(product: IProduct, action: 'save' | 'remove', cb = (u: IUser) => {}): void {
    const lang = this.languagesService.getCurrentLangCode();
    this.checkPermission(() => {
      if (action === 'save') {
        // this.analyticsService.ecTrackAddRemoveToCart(company, true);
        this.saveProductGQL.mutate({
          productId: product._id,
          lang: lang
        }, { update: (cache) => this.clearSavedProductsCache(cache) })
        .pipe(
          catchError(e => { throw e }),
          map(res => res.data.saveProduct)
        )
        .subscribe({
          next: u => cb(u),
          error: e => console.error(e)
        })
      } 
      else {
        // this.analyticsService.ecTrackAddRemoveToCart(company, false);
        this.removeProductGQL.mutate({
          productId: product._id,
          lang: lang
        }, { update: (cache) => this.clearSavedProductsCache(cache) })
        .pipe(
          catchError(e => { throw e }),
          map(res => res.data.removeProduct)
        )
        .subscribe({
          next: u => cb(u),
          error: e => console.error(e)
        })
      }
    });
  }

  isSavedProduct( productId: string ): boolean {
    if (!this._currentUser) 
      return false;
    return !!this._currentUser.profile.savedProducts.find(p => p._id === productId)
  }

  private clearSavedProductsCache(cache: any): void {
    const key = `^savedProductsPage:.*\"userId\":\"${this.currentUserId}\".*`;
    clearByRegEx(cache,key);
    cache.gc();
  }


    toggleSaveNews(news: INews, action: 'save' | 'remove', cb = (u: IUser) => {}): void {
    this.checkPermission(() => {
      if (action === 'save') {
        this.saveNewsGQL.mutate({
          newsId: news._id
        }, { update: (cache) => this.clearSavedNewsCache(cache) })
        .pipe(
          catchError(e => { throw e }),
          map(res => res.data.saveNews)
        )
        .subscribe({
          next: u => cb(u),
          error: e => console.error(e)
        })
      } 
      else {
        this.removeNewsGQL.mutate({
          newsId: news._id
        }, { update: (cache) => this.clearSavedNewsCache(cache) })
        .pipe(
          catchError(e => { throw e }),
          map(res => res.data.removeNews)
        )
        .subscribe({
          next: u => cb(u),
          error: e => console.error(e)
        })
      }
    });
  }

  isSavedNews( newsId: string ): boolean {
    if (!this._currentUser) 
      return false;
    return !!this._currentUser.profile.savedNews.find(p => p._id === newsId)
  }

  private clearSavedNewsCache(cache: any): void {
    const key = `^savedNewsPage:.*\"userId\":\"${this.currentUserId}\".*`;
    clearByRegEx(cache,key);
    cache.gc();
  }

}
